/**
 * The chord recogniser contract.
 *
 * This file is the seam between the DSP core and everything else. Two rules hold it:
 *
 *  1. The recogniser emits **pitch classes only**. It has no opinion about spelling,
 *     key, or what the user meant. `respell()` names notes later, at session level.
 *  2. Session logic consumes **only** `{ type: 'chord' }` events. `state` and
 *     `diagnostics` exist for the tuning UI; a reducer that reacts to them will
 *     re-render at 10 Hz for no benefit.
 *
 * `packages/shared` is DOM-free, so `MediaStream` cannot be referenced here. See
 * `MediaStreamLike`.
 */

import type { PitchClass, SeventhType, TriadQuality } from '../chord/types.js';

/**
 * The structural subset of the DOM's `MediaStream` that the recogniser contract needs.
 * A real `MediaStream` satisfies this, so client code passes one straight in; declaring
 * it here keeps `lib.dom` out of this package.
 */
export interface MediaStreamLike {
  readonly id: string;
  getAudioTracks(): unknown[];
}

/** One candidate reading of the sounding harmony. */
export interface ChordHypothesis {
  pitchClasses: PitchClass[];
  rootPc: PitchClass;
  bassPc: PitchClass;
  quality: TriadQuality;
  seventh?: SeventhType;
  /** 0..1 — template-match strength, already normalised. */
  score: number;
}

/** A recognised change of harmony, at the moment it was confirmed. */
export interface DetectedChord {
  best: ChordHypothesis;
  /** Up to 3, descending by score. Feeds the "did you mean…?" correction affordance. */
  alternatives: ChordHypothesis[];
  /** 12 bins — the integrated chroma at emission, kept so a correction can be re-scored. */
  chroma: number[];
  /** `best.score` with the margin over the runner-up factored in, 0..1. */
  confidence: number;
  /** `performance.now()` milliseconds at emission. */
  timestamp: number;
  /** First frame above the energy gate. `timestamp - onsetTimestamp` is the felt latency. */
  onsetTimestamp: number;
}

/**
 * Where the stability machine is.
 *
 * `silence` → nothing above the gate. `gathering` → energy present, not yet a stable set.
 * `candidate` → a hypothesis is leading but has not held for `confirmFrames`.
 * `stable` → emitted, holding. `reevaluating` → the set is changing under a held chord,
 * which is what distinguishes an arpeggio filling out a voicing from a new chord.
 */
export type StabilityStateName = 'silence' | 'gathering' | 'candidate' | 'stable' | 'reevaluating';

export type RecognizerEvent =
  /** A NEW harmony. The only event session logic consumes. */
  | { type: 'chord'; detected: DetectedChord }
  /** Energy fell back to silence. Not an error, and not a chord boundary on its own. */
  | { type: 'release' }
  | { type: 'state'; state: StabilityStateName }
  /** ~10 Hz. Tuning UI only — never drive session state from this. */
  | { type: 'diagnostics'; frame: DiagnosticsFrame };

/** Everything the tuning screen needs to explain why the recogniser did what it did. */
export interface DiagnosticsFrame {
  chromaInstant: number[];
  chromaIntegrated: number[];
  energyDb: number;
  flux: number;
  bassPc: PitchClass | null;
  state: StabilityStateName;
  /** Top 5, descending by score. */
  topHypotheses: ChordHypothesis[];
  framesPerSecond: number;
  lastEmissionLatencyMs: number | null;
}

/**
 * Every knob, all runtime-tunable.
 *
 * These are not constants hidden in the DSP: an acoustic piano in a real room needs
 * them adjusted by ear, on the device, while playing. `setParams` takes effect on the
 * next frame — no restart, no dropped audio.
 */
export interface RecognizerParams {
  /** Below this, the frame counts as silence. */
  energyGateDb: number;
  /** 4096 | 8192 | 16384. Larger buys frequency resolution at the cost of latency. */
  fftSize: number;
  hopSize: number;
  /** Half-life of the leaky integrator that turns instantaneous chroma into held chroma. */
  chromaDecayHalfLifeMs: number;
  /** Spectral flux above this counts as an onset. */
  onsetFluxThreshold: number;
  /** How much of the integrated chroma an onset discards, 0..1. */
  onsetResetFraction: number;
  minCandidateFrames: number;
  confirmFrames: number;
  /** Notes arriving inside this window are treated as part of one chord, not a new one. */
  arpeggioWindowMs: number;
  /** Hypotheses scoring below this are never emitted. */
  scoreFloor: number;
  /** A new chord must beat the held one by this margin before it replaces it. */
  hysteresisMargin: number;
  reevalFrames: number;
  /** Minimum time between emissions. */
  refractoryMs: number;
  /**
   * Bottom of the peak search, in Hz. Everything below it is discarded before the chroma
   * fold and before the bass search — traffic, air handling, the floor, the piano's own
   * case. Raising it costs nothing in pitch-class terms until it passes the second
   * partial of the lowest note played, because a partial votes for its fundamental.
   */
  peakFloorHz: number;
  bassBandLowHz: number;
  bassBandHighHz: number;
  /** How far the bass band must stand above the rest to fix the bass pitch class. */
  bassProminenceDb: number;
  /**
   * How many of 2f, 3f, 4f a bass candidate must have before it is believed to be a note.
   * 0 is the pre-2026-08-16 behaviour: whatever is lowest and loud enough wins.
   */
  bassMinHarmonics: number;
  /** Weights for partials 1..n when folding the spectrum into chroma. */
  harmonicWeights: number[];
  /** Cost added for positing a seventh, to stop every triad becoming a seventh chord. */
  seventhPenalty: number;
}

/**
 * The recogniser itself.
 *
 * Implementations own their audio graph and their worklet. Nothing above this interface
 * should know whether the source is a microphone, a file, or a future MIDI stream —
 * which is why `start` accepts `null`.
 */
export interface ChordRecognizer {
  /** `null` is permitted so a non-`MediaStream` source (MIDI, test fixture) fits later. */
  start(stream: MediaStreamLike | null): Promise<void>;
  stop(): Promise<void>;
  /** Used while voice input holds the microphone. Keeps state; does not reset. */
  pause(): void;
  resume(): void;
  /** Returns an unsubscribe function. */
  on(listener: (e: RecognizerEvent) => void): () => void;
  getParams(): RecognizerParams;
  /** Takes effect on the next frame. */
  setParams(patch: Partial<RecognizerParams>): void;
}

export const DEFAULT_RECOGNIZER_PARAMS: Readonly<RecognizerParams> = Object.freeze({
  energyGateDb: -52,
  fftSize: 8192,
  hopSize: 2048,
  chromaDecayHalfLifeMs: 600,
  onsetFluxThreshold: 0.35,
  onsetResetFraction: 0.5,
  minCandidateFrames: 3,
  confirmFrames: 5,
  arpeggioWindowMs: 900,
  scoreFloor: 0.55,
  hysteresisMargin: 0.12,
  reevalFrames: 6,
  refractoryMs: 350,
  peakFloorHz: 60,
  bassBandLowHz: 60,
  bassBandHighHz: 350,
  bassProminenceDb: 8,
  bassMinHarmonics: 2,
  // Eight, not five. A piano bass note has a dozen or more audible partials and the ones
  // past the fifth used to vote only for themselves — pollution rather than evidence. The
  // tail is as light as it is because of one measurement: at 0.12/0.09/0.07 a bare C3+B♭3
  // dyad accumulated enough of a third pitch class to be emitted as A♯sus2/C, and "a
  // melody note before or after a chord is ignored" is a product principle rather than a
  // tuning preference.
  harmonicWeights: Object.freeze([1, 0.6, 0.4, 0.25, 0.15, 0.1, 0.06, 0.05]) as number[],
  seventhPenalty: 0.04,
});

/**
 * One row per tunable parameter, so the tuning screen can render a control per knob
 * from a single `.map()` and stay in step automatically when a knob is added.
 *
 * `kind` and `choices` are optional additions to the minimum `{key, label, min, max,
 * step, unit}` shape: `fftSize` is a fixed set of powers of two rather than a
 * continuum, and `harmonicWeights` is a vector whose elements share one range.
 */
export interface RecognizerParamDescriptor {
  key: keyof RecognizerParams;
  label: string;
  min: number;
  max: number;
  step: number;
  /** Display unit, empty string when the value is dimensionless. */
  unit: string;
  /** Defaults to `'number'` when absent. */
  kind?: 'number' | 'choice' | 'number-array';
  /** Present only when `kind === 'choice'`. */
  choices?: number[];
  /** Present only when `kind === 'number-array'` — the fixed length of the vector. */
  arrayLength?: number;
  /** One line of "what does moving this do", for the tuning screen. */
  help?: string;
}

export const RECOGNIZER_PARAM_DESCRIPTORS: readonly RecognizerParamDescriptor[] = Object.freeze([
  {
    key: 'energyGateDb',
    label: 'Energy gate',
    min: -80,
    max: -20,
    step: 1,
    unit: 'dB',
    help: 'Raise it in a noisy room; lower it if quiet playing is being missed.',
  },
  {
    key: 'fftSize',
    label: 'FFT size',
    min: 4096,
    max: 16384,
    step: 4096,
    unit: 'samples',
    kind: 'choice',
    choices: [4096, 8192, 16384],
    help: 'Bigger separates low notes better but adds latency.',
  },
  {
    key: 'hopSize',
    label: 'Hop size',
    min: 512,
    max: 8192,
    step: 512,
    unit: 'samples',
    help: 'Smaller means more frames per second and faster response, at more CPU.',
  },
  {
    key: 'chromaDecayHalfLifeMs',
    label: 'Chroma half-life',
    min: 100,
    max: 2000,
    step: 25,
    unit: 'ms',
    help: 'How long a struck note keeps contributing. Longer suits rolled chords.',
  },
  {
    key: 'onsetFluxThreshold',
    label: 'Onset flux threshold',
    min: 0,
    max: 1,
    step: 0.01,
    unit: '',
    help: 'Lower detects gentler attacks; too low and sustain becomes an onset.',
  },
  {
    key: 'onsetResetFraction',
    label: 'Onset reset',
    min: 0,
    max: 1,
    step: 0.05,
    unit: '',
    help: 'How much history an onset throws away. 1 forgets the previous chord entirely.',
  },
  {
    key: 'minCandidateFrames',
    label: 'Minimum candidate frames',
    min: 1,
    max: 20,
    step: 1,
    unit: 'frames',
    help: 'Frames a hypothesis must lead before it is even considered.',
  },
  {
    key: 'confirmFrames',
    label: 'Confirm frames',
    min: 1,
    max: 30,
    step: 1,
    unit: 'frames',
    help: 'Frames a hypothesis must hold before it is emitted. The main latency knob.',
  },
  {
    key: 'arpeggioWindowMs',
    label: 'Arpeggio window',
    min: 0,
    max: 3000,
    step: 50,
    unit: 'ms',
    help: 'Notes arriving inside this window grow the current chord instead of starting one.',
  },
  {
    key: 'scoreFloor',
    label: 'Score floor',
    min: 0,
    max: 1,
    step: 0.01,
    unit: '',
    help: 'Minimum match strength to emit anything at all.',
  },
  {
    key: 'hysteresisMargin',
    label: 'Hysteresis margin',
    min: 0,
    max: 0.5,
    step: 0.01,
    unit: '',
    help: 'How much better a rival must be to unseat the chord currently held.',
  },
  {
    key: 'reevalFrames',
    label: 'Re-evaluation frames',
    min: 1,
    max: 30,
    step: 1,
    unit: 'frames',
    help: 'How long the recogniser stays sceptical after the sounding set changes.',
  },
  {
    key: 'refractoryMs',
    label: 'Refractory period',
    min: 0,
    max: 2000,
    step: 25,
    unit: 'ms',
    help: 'Minimum gap between emissions. Stops trills producing a stream of chords.',
  },
  {
    key: 'peakFloorHz',
    label: 'Peak floor',
    min: 30,
    max: 120,
    step: 5,
    unit: 'Hz',
    help: 'Everything below this is treated as room noise. Lower it only for very low playing.',
  },
  {
    key: 'bassBandLowHz',
    label: 'Bass band low',
    min: 20,
    max: 200,
    step: 5,
    unit: 'Hz',
    help: 'Bottom of the band searched for the bass note.',
  },
  {
    key: 'bassBandHighHz',
    label: 'Bass band high',
    min: 100,
    max: 800,
    step: 10,
    unit: 'Hz',
    help: 'Top of that band. Too high and the tenor voice starts winning.',
  },
  {
    key: 'bassProminenceDb',
    label: 'Bass prominence',
    min: 0,
    max: 30,
    step: 1,
    unit: 'dB',
    help: 'How far the bass must stand out before an inversion is claimed.',
  },
  {
    key: 'bassMinHarmonics',
    label: 'Bass harmonics required',
    min: 0,
    max: 3,
    step: 1,
    unit: 'partials',
    help: 'How many of 2f/3f/4f a low peak needs before it counts as the bass. 0 trusts any peak.',
  },
  {
    key: 'harmonicWeights',
    label: 'Harmonic weights',
    min: 0,
    max: 1,
    step: 0.05,
    unit: '',
    kind: 'number-array',
    arrayLength: 8,
    help: 'Contribution of partials 1–8 when folding the spectrum into chroma.',
  },
  {
    key: 'seventhPenalty',
    label: 'Seventh penalty',
    min: 0,
    max: 0.5,
    step: 0.005,
    unit: '',
    help: 'Raise it if plain triads keep being heard as seventh chords.',
  },
] as const satisfies readonly RecognizerParamDescriptor[]);

/**
 * Merge a patch onto a parameter set, clamping every value to the range its descriptor
 * declares. Implementations should route `setParams` through this so a bad slider value
 * or a stale persisted value can never put the DSP into an impossible state.
 */
export function mergeRecognizerParams(
  base: RecognizerParams,
  patch: Partial<RecognizerParams>,
): RecognizerParams {
  const next: RecognizerParams = { ...base, ...patch };
  for (const descriptor of RECOGNIZER_PARAM_DESCRIPTORS) {
    const value = next[descriptor.key];
    if (typeof value === 'number') {
      if (descriptor.kind === 'choice' && descriptor.choices) {
        if (!descriptor.choices.includes(value)) {
          (next[descriptor.key] as number) = base[descriptor.key] as number;
        }
        continue;
      }
      (next[descriptor.key] as number) = Math.min(descriptor.max, Math.max(descriptor.min, value));
    } else if (Array.isArray(value)) {
      (next[descriptor.key] as number[]) = value.map((entry) =>
        Math.min(descriptor.max, Math.max(descriptor.min, entry)),
      );
    }
  }
  return next;
}
